import Input from "components/Input";
import React from "react";
import { epdEditorStore } from "../EPDEditorStore";
import { EPDDataType, EPDFieldNameEnum } from "api-client";
import { useTranslation } from "react-i18next";
import { observer } from "mobx-react-lite";
import CustomDatePicker from "components/DatePicker";
import EPDFile from "./EPDFile";
import DynamicDropdown from "components/DynamicDropdown";
import ComboboxSelect from "components/ComboboxSelect";
import moment from "moment";

const pageSize = 100;
const GeneralInformation = () => {
  const { t, i18n } = useTranslation();
  const isEng = i18n.language === "en";

  React.useEffect(() => {
    if (epdEditorStore.filterOptions?.[EPDFieldNameEnum.DataType]?.length)
      return;
    epdEditorStore.getFilterOptions();
    epdEditorStore.fetchManufacturers(1, pageSize, "");
  }, []);

  const epdTypes = Object.values(EPDDataType).map((item) => ({
    id: item,
    name: t("EPDEditor.types." + item),
    value: item,
    description: t("EPDEditor.types." + item + "Desc"),
  }));

  const fetchManufacturers = async (
    searchTerm: string,
    page?: number,
    pageSize?: number
  ) => {
    return await epdEditorStore
      .fetchManufacturers(page ?? 1, pageSize ?? 100, searchTerm)
      .then((response) => {
        return {
          items: response.items,
          count: response.count,
        };
      });
  };

  const handlePublishDateChange = (value: string | undefined | Date) => {
    epdEditorStore.onChangeData("publishing_date", value);
    const expiryYear = moment(value).add(5, "years").year();
    epdEditorStore.onChangeData("expiry_year", String(expiryYear));
  };

  return (
    <div className="border-b border-gray-200 pb-6">
      <div className="text-base font-semibold mb-4">
        {t("productEditor.generalInformation")}
      </div>
      <div className="flex gap-4 w-full justify-between mb-3">
        <Input
          labelName="Name"
          onChange={(value) => epdEditorStore.onChangeData("name", value)}
          value={epdEditorStore.data.name}
          placeholder={t("productEditor.enterName")}
          containerClassName="w-full"
          required
        />
        <DynamicDropdown
          labelName={t("EPDEditor.manufacturer")}
          placeholder={t("EPDEditor.searchOrSelect")}
          helper={t("EPDEditor.manufacturerHelper")}
          required
          pageSize={pageSize}
          fetchOptions={fetchManufacturers}
          createOption={epdEditorStore.createManufacturer}
          value={epdEditorStore.data.manufacturer}
          onSelect={(item) =>
            epdEditorStore.onChangeData("manufacturer", item?.name)
          }
        />
      </div>

      <div className="flex gap-4 w-full justify-between mb-3">
        <CustomDatePicker
          value={epdEditorStore.data.publishing_date}
          inputClassName="h-9"
          name="publishing_date"
          dateFormat="DD.MM.yyyy"
          placeholder={isEng ? "DD.MM.YYYY" : "DD.MM.JJJJ"}
          onChange={handlePublishDateChange}
          label={t("EPDEditor.publishedOn")}
          required
        />
        <CustomDatePicker
          value={epdEditorStore.data.expiry_year}
          inputClassName="h-9"
          name="expiry_year"
          dateFormat="yyyy"
          placeholder={isEng ? "YYYY" : "JJJJ"}
          yearPicker
          onChange={(value) =>
            epdEditorStore.onChangeData("expiry_year", value)
          }
          label={t("EPDEditor.validUntil")}
          required
        />
      </div>

      <div className="flex gap-4 w-full justify-between">
        <ComboboxSelect
          labelName="Typ"
          displayKey="name"
          descriptionKey="description"
          placeholder={t("EPDEditor.pleaseSelect")}
          containerClassName="w-full"
          optionsClassName="min-h-[370px]"
          height="middle"
          selectedOption={epdTypes.find(
            (rec) => rec.value === epdEditorStore.data.data_type
          )}
          items={epdTypes}
          required
          handleChange={(value) =>
            epdEditorStore.onChangeData("data_type", value.value)
          }
        />
        <Input
          labelName={t("EPDEditor.registerationNumber")}
          placeholder={t("EPDEditor.pleaseEnter")}
          helper={t("EPDEditor.registrationHelper")}
          onChange={(value) =>
            epdEditorStore.onChangeData("registration_number", value)
          }
          containerClassName="w-full"
          value={epdEditorStore.data.registration_number}
        />
      </div>

      <Input
        labelName="Version"
        onChange={(value) => epdEditorStore.onChangeData("version", value)}
        containerClassName="w-full"
        placeholder={t("EPDEditor.pleaseEnter")}
        value={
          epdEditorStore.data.version ? String(epdEditorStore.data.version) : ""
        }
        helper={t("EPDEditor.versionHelper")}
      />

      <EPDFile />
    </div>
  );
};

export default observer(GeneralInformation);
